import { useState, useEffect, useCallback } from "react";
import {
  Users,
  TrendingUp,
  CheckCircle,
  BarChart2,
  RefreshCw,
  Download,
  Phone,
  Lock,
  User,
  Eye,
  EyeOff,
  LogOut,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  ArrowRight,
} from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const api = axios.create({ baseURL: `${BACKEND}/api`, timeout: 4000 });

// Credenciais padrão de fallback caso o backend esteja offline
const DEFAULT_USER = process.env.REACT_APP_ADMIN_USER || "admin";
const DEFAULT_PASS = process.env.REACT_APP_ADMIN_PASS || "laserday2025";
const DEFAULT_KEY = "laserday2024admin";

// ─── Helpers de Formatação ─────────────────────────────────

const fmt = (dt) => {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtPhone = (p) => {
  if (!p) return "—";
  const d = String(p).replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return p;
};

const CTA_LABELS = {
  hero: "Hero (Topo)",
  offer: "Oferta",
  final_cta: "Final CTA",
  finalcta: "Final CTA",
  floating: "Botão Flutuante",
  header: "Menu Topo",
  mobile_menu: "Menu Mobile",
  benefits: "Benefícios",
  areas_list: "Lista de Áreas",
  footer: "Rodapé",
  embedded_form: "Formulário na Página",
  unknown: "Geral / Desconhecido",
};

// ─── Calculador de Estatísticas Locais ─────────────────────

const computeLocalStats = (leadsList) => {
  const total = leadsList.length;
  const completed = leadsList.filter((l) => l.completed).length;
  const conv = total > 0 ? Math.round((completed / total) * 100) : 0;

  const step1 = leadsList.filter((l) => (l.step || 1) >= 1).length;
  const step2 = leadsList.filter((l) => (l.step || 1) >= 2 || l.area).length;
  const step3 = leadsList.filter((l) => (l.step || 1) >= 3 || l.first_time).length;
  const step4 = completed;

  const ctaMap = {};
  leadsList.forEach((l) => {
    const o = l.cta_origin || "unknown";
    ctaMap[o] = (ctaMap[o] || 0) + 1;
  });
  const by_cta = Object.entries(ctaMap).map(([origin, count]) => ({ origin, count }));

  const areaMap = {};
  leadsList.forEach((l) => {
    if (l.area) areaMap[l.area] = (areaMap[l.area] || 0) + 1;
  });
  const by_area = Object.entries(areaMap).map(([area, count]) => ({ area, count }));

  return {
    total_leads: total,
    completed,
    conversion_rate: conv,
    by_step: { step_1: step1, step_2: step2, step_3: step3, step_4: step4 },
    by_cta,
    by_area,
  };
};

// ─── Componentes de UI ────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = "#9A4A38" }) => (
  <div className="flex flex-col gap-2 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-[#E9C4B6]/30">
    <div className="flex items-center gap-2 text-xs font-semibold text-[#6E5B55] uppercase tracking-wider">
      <Icon className="h-4 w-4" style={{ color }} />
      {label}
    </div>
    <p className="text-3xl font-black text-[#2D1C18]">{value}</p>
    {sub && <p className="text-xs text-[#9A7A72]">{sub}</p>}
  </div>
);

const FunnelBar = ({ label, value, max }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="mb-1 flex justify-between text-sm">
        <span className="font-medium text-[#2D1C18]">{label}</span>
        <span className="font-bold text-[#9A4A38]">
          {value} <span className="text-xs text-[#9A7A72]">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-[#F5ECE1]">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-[#A9573F] to-[#E4A08B] transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const exportCSV = (leads) => {
  const headers = [
    "Data",
    "Nome",
    "Telefone",
    "Área",
    "1a vez",
    "Horário",
    "Origem CTA",
    "Dispositivo",
    "Concluído",
  ];
  const rows = leads.map((l) => [
    fmt(l.created_at || l.updated_at),
    l.name || "",
    fmtPhone(l.phone),
    l.area || "",
    l.first_time || "",
    l.preferred_time || "",
    CTA_LABELS[l.cta_origin] || l.cta_origin || "",
    l.device || "",
    l.completed ? "Sim" : "Não",
  ]);
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `leads-laserday-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Tela de Login (Acesso Restrito) ──────────────────────

const LoginScreen = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Por favor, preencha o usuário e a senha.");
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Tenta autenticar na API do backend
      let token = DEFAULT_KEY;
      let authenticatedUser = username.trim();

      try {
        const res = await api.post("/admin/login", {
          username: username.trim(),
          password: password.trim(),
        });
        if (res.data?.token) {
          token = res.data.token;
          authenticatedUser = res.data.username || authenticatedUser;
        }
      } catch (apiErr) {
        // Se a API retornou 401, rejeita imediatamente
        if (apiErr.response?.status === 401) {
          setError("Usuário ou senha incorretos.");
          setIsSubmitting(false);
          return;
        }
        // Se a API estiver offline/inacessível, valida localmente
        if (username.trim() !== DEFAULT_USER || password.trim() !== DEFAULT_PASS) {
          setError("Usuário ou senha incorretos.");
          setIsSubmitting(false);
          return;
        }
      }

      // Salva sessão
      const sessionData = {
        token,
        username: authenticatedUser,
        timestamp: Date.now(),
      };

      if (rememberMe) {
        localStorage.setItem("ld_auth_session", JSON.stringify(sessionData));
      } else {
        sessionStorage.setItem("ld_auth_session", JSON.stringify(sessionData));
      }

      onLoginSuccess(sessionData);
    } catch (err) {
      setError("Ocorreu um erro ao validar o acesso. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF5EE] px-4 font-[Manrope,sans-serif]">
      {/* Glow de fundo */}
      <div className="pointer-events-none fixed -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#E9B3A0]/25 blur-[120px]" />
      <div className="pointer-events-none fixed -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#B8654F]/15 blur-[120px]" />

      <div className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-xl ring-1 ring-[#E9C4B6]/40 sm:p-10">
        {/* Cabeçalho */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2D1C18] to-[#5A2A1F] text-[#FAF5EE] shadow-md">
            <Lock className="h-6 w-6 text-[#E9B3A0]" />
          </div>
          <p className="text-[11px] font-bold tracking-[0.25em] text-[#9A4A38] uppercase">
            Aisla Studio · Laser Day
          </p>
          <h1 className="mt-1 font-display text-2xl font-black text-[#2D1C18] sm:text-3xl">
            Acesso Restrito
          </h1>
          <p className="mt-2 text-xs text-[#6E5B55]">
            Digite suas credenciais de administrador para acessar o painel de leads e métricas.
          </p>
        </div>

        {/* Alerta de erro */}
        {error && (
          <div className="mt-5 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-xs font-medium text-red-700 ring-1 ring-red-200">
            <ShieldAlert className="h-4 w-4 shrink-0 text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#2D1C18] uppercase tracking-wider">
              Usuário
            </label>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-[#9A7A72]">
                <User className="h-4 w-4" />
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ex: admin"
                autoComplete="username"
                className="w-full rounded-xl border border-[#E9C4B6] bg-[#FAF5EE]/40 py-2.5 pl-10 pr-3 text-sm text-[#2D1C18] outline-none transition-all placeholder:text-[#9A7A72]/60 focus:border-[#9A4A38] focus:bg-white focus:ring-2 focus:ring-[#9A4A38]/20"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#2D1C18] uppercase tracking-wider">
              Senha
            </label>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-[#9A7A72]">
                <Lock className="h-4 w-4" />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full rounded-xl border border-[#E9C4B6] bg-[#FAF5EE]/40 py-2.5 pl-10 pr-10 text-sm text-[#2D1C18] outline-none transition-all placeholder:text-[#9A7A72]/60 focus:border-[#9A4A38] focus:bg-white focus:ring-2 focus:ring-[#9A4A38]/20"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-[#9A7A72] hover:text-[#2D1C18]"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-[#E9C4B6] text-[#9A4A38] focus:ring-[#9A4A38]"
              />
              <span className="text-xs text-[#6E5B55]">Lembrar de mim</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2D1C18] to-[#4F251C] py-3 text-sm font-bold text-[#FAF5EE] shadow-md transition-all hover:opacity-90 active:scale-[0.99] disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Verificando...</span>
              </>
            ) : (
              <>
                <span>Entrar no Painel</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = "";
              window.location.pathname = "/";
            }}
            className="text-xs font-semibold text-[#9A7A72] hover:text-[#2D1C18] transition-colors"
          >
            ← Voltar para a Landing Page
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────

export const AdminDashboard = () => {
  // Verifica se existe sessão salva
  const getSavedSession = () => {
    try {
      const stored = localStorage.getItem("ld_auth_session") || sessionStorage.getItem("ld_auth_session");
      if (stored) return JSON.parse(stored);
    } catch (_) {}
    return null;
  };

  const [session, setSession] = useState(getSavedSession);
  const [dataSource, setDataSource] = useState("api"); // "api" | "local"
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (token) => {
    setLoading(true);
    const key = token || session?.token || DEFAULT_KEY;

    try {
      const [sRes, lRes] = await Promise.all([
        api.get(`/admin/stats?key=${key}`),
        api.get(`/admin/leads?key=${key}&page=1&limit=100`),
      ]);
      setStats(sRes.data);
      setLeads(lRes.data.leads || []);
      setDataSource("api");
    } catch (e) {
      // Se deu 401 no token, pode ser sessão inválida
      if (e.response?.status === 401) {
        console.warn("[AdminDashboard] Chave inválida ou expirada.");
      }
      // Fallback para dados locais salvos no navegador
      console.warn("[AdminDashboard] Usando dados locais salvos:", e.message);
      const rawLeads = localStorage.getItem("ld_leads");
      const localLeads = rawLeads ? JSON.parse(rawLeads) : [];
      setLeads(localLeads);
      setStats(computeLocalStats(localLeads));
      setDataSource("local");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      loadData(session.token);
    }
  }, [session, loadData]);

  const handleLogout = () => {
    localStorage.removeItem("ld_auth_session");
    sessionStorage.removeItem("ld_auth_session");
    setSession(null);
  };

  const handleClearLocal = () => {
    if (window.confirm("Deseja apagar os leads de teste salvos neste navegador?")) {
      localStorage.removeItem("ld_leads");
      localStorage.removeItem("ld_events");
      loadData(session?.token);
    }
  };

  const handleClearServer = async () => {
    if (window.confirm("Atenção: Deseja apagar todos os leads de teste registrados no servidor?")) {
      try {
        await api.post(`/admin/leads/delete?key=${session?.token || DEFAULT_KEY}`, { all: true });
        loadData(session?.token);
      } catch (err) {
        alert("Erro ao excluir leads do servidor.");
      }
    }
  };

  // Se NÃO está logado, renderiza a tela de login restrito
  if (!session) {
    return <LoginScreen onLoginSuccess={(newSession) => setSession(newSession)} />;
  }

  return (
    <div className="min-h-screen bg-[#FAF5EE] font-[Manrope,sans-serif]">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-[#E9C4B6]/30 bg-[#FAF5EE]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-black text-[#2D1C18]">⚡ Laser Day — Admin</h1>
              <span
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  dataSource === "api"
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }`}
                title={dataSource === "api" ? "Conectado ao servidor MongoDB" : "Modo navegador (dados salvos localmente)"}
              >
                {dataSource === "api" ? "● Servidor Online" : "● Modo Navegador"}
              </span>
            </div>
            <p className="text-xs text-[#9A7A72]">
              Painel de leads e funil do WhatsApp · Conectado como <strong className="text-[#2D1C18]">{session.username || "admin"}</strong>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = "";
                window.location.pathname = "/";
              }}
              className="rounded-lg border border-[#E9C4B6] bg-white px-3 py-2 text-xs font-semibold text-[#2D1C18] hover:bg-[#FAF5EE] transition-colors"
            >
              ← Ver Landing Page
            </a>

            <button
              onClick={() => exportCSV(leads)}
              disabled={leads.length === 0}
              className="flex items-center gap-1.5 rounded-lg bg-[#2D1C18] px-3 py-2 text-xs font-semibold text-[#FAF5EE] transition-opacity hover:opacity-80 disabled:opacity-30"
            >
              <Download className="h-3.5 w-3.5" /> Exportar CSV
            </button>

            <button
              onClick={() => loadData(session?.token)}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg border border-[#E9C4B6] bg-white px-3 py-2 text-xs font-semibold text-[#2D1C18] transition-opacity hover:opacity-70 disabled:opacity-40"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
            </button>

            {leads.length > 0 && (
              <button
                onClick={dataSource === "api" ? handleClearServer : handleClearLocal}
                title="Limpar leads de teste"
                className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-2 text-xs font-medium text-red-600 hover:bg-red-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}

            {/* Botão de Logout */}
            <button
              onClick={handleLogout}
              title="Encerrar sessão"
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {loading && !stats ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-[#9A4A38]" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard icon={Users} label="Total de leads" value={stats?.total_leads ?? 0} sub="iniciaram o funil" />
              <StatCard icon={CheckCircle} label="Concluídos" value={stats?.completed ?? 0} sub="chegaram ao WhatsApp" color="#1E7E45" />
              <StatCard icon={TrendingUp} label="Conversão" value={`${stats?.conversion_rate ?? 0}%`} sub="iniciaram → concluíram" color="#7F3628" />
              <StatCard icon={BarChart2} label="Etapa 1" value={stats?.by_step?.step_1 ?? 0} sub="responderam área" />
            </div>

            <div className="mb-8 grid gap-6 lg:grid-cols-2">
              {/* Funil */}
              <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-[#E9C4B6]/30">
                <h2 className="mb-4 text-sm font-bold uppercase tracking-widest text-[#9A4A38]">
                  📊 Funil por etapa
                </h2>
                <FunnelBar label="Etapa 1 — Área escolhida" value={stats?.by_step?.step_1 ?? 0} max={stats?.total_leads ?? 1} />
                <FunnelBar label="Etapa 2 — 1ª vez?" value={stats?.by_step?.step_2 ?? 0} max={stats?.total_leads ?? 1} />
                <FunnelBar label="Etapa 3 — Horário preferido" value={stats?.by_step?.step_3 ?? 0} max={stats?.total_leads ?? 1} />
                <FunnelBar label="Etapa 4 — Nome + Telefone (Concluído)" value={stats?.completed ?? 0} max={stats?.total_leads ?? 1} />
              </div>

              {/* Origem CTAs + Área */}
              <div className="flex flex-col gap-6">
                <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-[#E9C4B6]/30">
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-[#9A4A38]">
                    🎯 Origem do clique
                  </h2>
                  {stats?.by_cta?.length ? (
                    stats.by_cta.map((c) => (
                      <div key={c.origin} className="mb-2 flex items-center justify-between text-sm">
                        <span className="text-[#6E5B55]">{CTA_LABELS[c.origin] || c.origin}</span>
                        <span className="font-bold text-[#2D1C18]">{c.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-[#9A7A72]">Nenhum clique registrado ainda.</p>
                  )}
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-[#E9C4B6]/30">
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-[#9A4A38]">
                    ✨ Áreas mais procuradas
                  </h2>
                  {stats?.by_area?.length ? (
                    stats.by_area.map((a) => (
                      <div key={a.area} className="mb-2 flex items-center justify-between text-sm">
                        <span className="text-[#6E5B55]">{a.area}</span>
                        <span className="font-bold text-[#9A4A38]">{a.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-[#9A7A72]">Nenhuma área selecionada ainda.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tabela de Leads */}
            <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-[#E9C4B6]/30">
              <div className="flex items-center justify-between border-b border-[#E9C4B6]/20 px-6 py-4">
                <h2 className="text-sm font-bold uppercase tracking-widest text-[#2D1C18]">
                  📋 Todos os Leads ({leads.length})
                </h2>
                <span className="text-xs text-[#9A7A72]">
                  Ordenados pelos mais recentes
                </span>
              </div>

              {leads.length === 0 ? (
                <div className="py-16 text-center">
                  <p className="text-base font-semibold text-[#6E5B55]">Nenhum lead registrado ainda.</p>
                  <p className="mt-1 text-xs text-[#9A7A72]">
                    Faça um teste clicando em qualquer botão de WhatsApp na landing page!
                  </p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.hash = "";
                      window.location.pathname = "/";
                    }}
                    className="mt-4 inline-block rounded-xl bg-[#2D1C18] px-4 py-2 text-xs font-semibold text-[#FAF5EE]"
                  >
                    Ir para o site e testar
                  </a>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="border-b border-[#E9C4B6]/20 bg-[#FAF5EE] text-[#9A7A72] uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Data/Hora</th>
                        <th className="px-4 py-3">Nome</th>
                        <th className="px-4 py-3">Telefone</th>
                        <th className="px-4 py-3">Área</th>
                        <th className="px-4 py-3">1ª vez?</th>
                        <th className="px-4 py-3">Horário</th>
                        <th className="px-4 py-3">Origem</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E9C4B6]/15">
                      {leads.map((lead, idx) => (
                        <tr key={lead.id || lead.session_id || idx} className="hover:bg-[#FAF5EE]/50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-[#6E5B55]">
                            {fmt(lead.created_at || lead.updated_at)}
                          </td>
                          <td className="px-4 py-3 font-bold text-[#2D1C18] whitespace-nowrap">
                            {lead.name || <span className="text-gray-400 italic font-normal">Não informado</span>}
                          </td>
                          <td className="px-4 py-3 font-mono font-medium text-[#2D1C18] whitespace-nowrap">
                            {lead.phone ? (
                              <a
                                href={`https://wa.me/55${lead.phone.replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#1E7E45] hover:underline"
                              >
                                <Phone className="h-3 w-3" />
                                {fmtPhone(lead.phone)}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">Abandonou antes</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[#6E5B55]">
                            {lead.area || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[#6E5B55]">
                            {lead.first_time || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[#6E5B55]">
                            {lead.preferred_time || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="rounded-md bg-[#FAF5EE] px-2 py-0.5 font-medium text-[#6E5B55]">
                              {CTA_LABELS[lead.cta_origin] || lead.cta_origin || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {lead.completed ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-bold text-green-700">
                                <CheckCircle className="h-3 w-3" /> Concluído
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                Etapa {lead.step || 1} (abandono)
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};
